import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Share2, Copy } from "lucide-react";
import { api, API, fmtError } from "../lib/api";
import { SEO } from "../components/SEO";
import { TrackCard } from "../components/TrackCard";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";

const Artiste = () => {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setArtist(null);
    setError("");
    api
      .get(`/artistes/${slug}`)
      .then((r) => setArtist(r.data))
      .catch((e) => setError(fmtError(e)));
    // L'URL visible devient l'URL de partage (carte riche avec visuel personnalisé)
    window.history.replaceState(null, "", `${API}/share/artist/${slug}`);
  }, [slug]);

  if (error)
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="artiste-error">
        <p className="text-sm uppercase tracking-[0.25em] text-zinc-500">{error}</p>
      </main>
    );
  if (!artist)
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="artiste-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-amber-500" />
      </main>
    );

  // URL servie par le backend : balises Open Graph (nom + visuel personnalisé) lues
  // par Facebook, X, LinkedIn… puis redirection vers cette page.
  const artistShareUrl = `${API}/share/artist/${slug}`;

  return (
    <main data-testid="artiste-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
      {artist && (
        <SEO title={artist.artist_name} path={`/artiste/${artist.slug}`}
             description={(artist.bio || `Écoutez les musiques de ${artist.artist_name}, en licence sur La Console des Artistes.`).slice(0, 155)}
             image={`${API}/share/artist/${artist.slug}/image`} />
      )}
      <Link
        to="/artistes"
        data-testid="artiste-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 transition-colors hover:text-amber-400"
      >
        <ArrowLeft className="h-4 w-4" /> Tous les artistes
      </Link>

      <Reveal>
        <div className="mt-10 border-b border-white/[0.08] pb-12">
          <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Artiste résident</p>
          <h1 data-testid="artiste-name" className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-6xl">
            {artist.artist_name}
          </h1>
          {artist.bio && (
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base" data-testid="artiste-bio">
              {artist.bio}
            </p>
          )}
          <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-zinc-500">
            {artist.tracks.length} morceau{artist.tracks.length > 1 ? "x" : ""} disponible{artist.tracks.length > 1 ? "s" : ""} en licence
          </p>
          <div className="mt-6 flex items-center gap-3" data-testid="artist-share-row">
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Partager cet artiste</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(artistShareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="artist-share-facebook"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-amber-500 hover:text-amber-400"
              title="Partager sur Facebook"
            >
              <span className="text-xs font-bold">f</span>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${artist.artist_name} — musiques en licence libre de droit`)}&url=${encodeURIComponent(artistShareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="artist-share-x"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-amber-500 hover:text-amber-400"
              title="Partager sur X"
            >
              <Share2 className="h-4 w-4" />
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(artistShareUrl)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="artist-share-linkedin"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-amber-500 hover:text-amber-400"
              title="Partager sur LinkedIn"
            >
              <span className="text-xs font-bold">in</span>
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(artistShareUrl);
                toast.success("Lien copié !");
              }}
              data-testid="artist-share-copy"
              className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-amber-500 hover:text-amber-400"
              title="Copier le lien"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Reveal>

      {artist.tracks.length === 0 ? (
        <p className="mt-16 text-center text-sm uppercase tracking-[0.25em] text-zinc-500" data-testid="artiste-empty">
          Aucun morceau publié pour le moment
        </p>
      ) : (
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="artiste-tracks">
          {artist.tracks.map((t, i) => (
            <TrackCard key={t.track_id} track={t} index={i} />
          ))}
        </div>
      )}
    </main>
  );
};

export default Artiste;
